import {type FC, type RefObject, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import StudioWaitImg from '../../../assets/broadcast/studio-wait.jpg';
import Hls from 'hls.js';
import useStreamingStore from '../../../store/useStreamingStore.ts';
import type {JitsiTrackState} from '../types/lib-jitsi-meet';
import {checkNetworkStatus, type ILayOut, type TNetworkStatus} from '../../../utils/StreamingUtil.ts';
import {StationBadge} from './StationBadge.tsx';
import {findCurrentScheduleIndex} from '../../../utils/DateUtil.ts';
import NetworkStatus from './NetworkStatus.tsx';
import useCenterStore from '../../../store/useCenterStore.ts';
import ConnectionStatus from "./ConnectionStatus.tsx";

interface StudioCellProps {
    studioElemRef: RefObject<HTMLVideoElement | null>;
    studioTrack: JitsiTrackState;
    gridInfo: ILayOut;
    isWatching?: boolean;
}

const StudioCell: FC<StudioCellProps> = ({studioElemRef, studioTrack, gridInfo, isWatching = false}) => {
    const [isLiveReady, setIsLiveReady] = useState<boolean>(false);
    const [playerKey, setPlayerKey] = useState(0);
    const {videoMode} = useCenterStore();
    const {scheduledData, isHlsServerOn, streamingPollingData} = useStreamingStore();
    const hlsVideoRef = useRef<HTMLVideoElement>(null);
    const hlsRef = useRef<Hls | null>(null);

    useEffect(() => {
        if (isHlsServerOn) {
            setIsLiveReady(false);
            setPlayerKey((prev) => prev + 1);
        }
    }, [isHlsServerOn]);

    const [scheduledIndex, setScheduledIndex] = useState<number>(-1);
    useEffect(() => {
        const index = findCurrentScheduleIndex(scheduledData);
        setScheduledIndex(index);
    }, [scheduledData]);

    const currentSchedule = scheduledIndex !== -1 ? scheduledData[scheduledIndex] : null;
    const currentStreamUrl = useMemo(() => currentSchedule?.stream_url ?? null, [currentSchedule?.stream_url]);

    useEffect(() => {
        if (currentStreamUrl) {
            setPlayerKey((prev) => prev + 1);
            setIsLiveReady(false);
        }
    }, [currentStreamUrl]);

    const HLS_URL = currentStreamUrl ?? 'https://rlfgdocrk.toastcdn.net/test/test/master.m3u8';
    const POLLING_INTERVAL = 10000;

    const [status, setStatus] = useState<TNetworkStatus>('대기');
    const badCountRef = useRef(0);
    const HYSTERESIS_THRESHOLD = 3;

    const check = useCallback(async () => {
        const result = await checkNetworkStatus(HLS_URL);
        if (result === '나쁨') {
            badCountRef.current += 1;
            if (badCountRef.current >= HYSTERESIS_THRESHOLD) {
                setStatus('나쁨');
            }
        } else {
            badCountRef.current = 0;
            setStatus(result);
        }
    }, [HLS_URL]);

    useEffect(() => {
        check();
        let interval: ReturnType<typeof setTimeout>;
        if (videoMode === "high-quality") {
            interval = setInterval(check, POLLING_INTERVAL);
        }
        return () => clearInterval(interval);
    }, [check, videoMode]);

    // const shouldUseHls = (status === '보통' || status === '좋음') && videoMode === 'high-quality' && !!currentStreamUrl;
    const shouldUseHls = false;

    useEffect(() => {
        if (!shouldUseHls || !hlsVideoRef.current || !currentStreamUrl) {
            return;
        }

        let isCancelled = false;
        const video = hlsVideoRef.current;
        const hlsUrl = currentStreamUrl;

        // 기존 HLS 인스턴스 정리
        if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
        }

        if (Hls.isSupported()) {
            const hls = new Hls({
                enableWorker: true,
                lowLatencyMode: false,
                liveSyncDurationCount: 3,
                liveMaxLatencyDurationCount: 10,
                liveBackBufferLength: 30,
            });

            hls.loadSource(hlsUrl);
            hls.attachMedia(video);

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                if (isCancelled) return;
                console.log('🎬 [HLS] ✅ 매니페스트 파싱 완료, 재생 시작');
                video.play().catch(e => {
                    if (e.name !== 'AbortError') {
                        console.error('🎬 [HLS] 재생 실패:', e);
                    }
                });
                setIsLiveReady(true);
            });

            hls.on(Hls.Events.ERROR, (event, data) => {
                if (isCancelled) return;
                console.error('🎬 [HLS] ❌ 에러:', data.type, data.details);
                if (data.fatal) {
                    switch (data.type) {
                        case Hls.ErrorTypes.NETWORK_ERROR:
                            console.log('🎬 [HLS] 네트워크 에러, 재시도...');
                            hls.startLoad();
                            break;
                        case Hls.ErrorTypes.MEDIA_ERROR:
                            console.log('🎬 [HLS] 미디어 에러, 복구 시도...');
                            hls.recoverMediaError();
                            break;
                        default:
                            hls.destroy();
                            break;
                    }
                }
            });

            hlsRef.current = hls;
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
            // Safari 네이티브 HLS 지원
            video.src = hlsUrl;
            video.addEventListener('loadedmetadata', () => {
                if (isCancelled) return;
                video.play().catch(e => {
                    if (e.name !== 'AbortError') {
                        console.error('🎬 [HLS] Safari 재생 실패:', e);
                    }
                });
            });
        }

        return () => {
            isCancelled = true;
            if (hlsRef.current) {
                console.log('🎬 [HLS] cleanup - destroy');
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
        };
    }, [shouldUseHls, currentStreamUrl, playerKey]);

    useEffect(() => {
        const studioVideoElement = studioElemRef.current;
        const studioAudioElement = document.getElementById('studio-cell-audio') as HTMLAudioElement;
        if (shouldUseHls) {
            studioTrack.videoTrack?.detach(studioVideoElement!);
            studioTrack.audioTrack?.detach(studioAudioElement!);
        } else {
            if (studioVideoElement && studioTrack.videoTrack) {
                studioTrack.videoTrack.attach(studioVideoElement);
            }
            if (studioTrack.audioTrack && studioAudioElement) {
                studioTrack.audioTrack.attach(studioAudioElement);
            }
        }
        return () => {
            studioTrack.videoTrack?.detach(studioVideoElement!);
            studioTrack.audioTrack?.detach(studioAudioElement!);
        };
    }, [studioTrack, studioElemRef, shouldUseHls]);

    const containerStyle = {
        gridColumn: `${gridInfo.studioCol} / span ${gridInfo.studioColSpan}`,
        gridRow: `${gridInfo.studioRow} / span ${gridInfo.studioRowSpan}`,
        position: 'relative' as const,
        width: '100%',
        overflow: 'hidden',
        borderRadius: '10px',
        padding: '10px',
        justifySelf: gridInfo.studioJustifySelf,
    };

    const backgroundStyle = {
        objectFit: 'cover' as const,
        aspectRatio: '16/9',
        position: 'inherit' as const,
        zIndex: 2,
    };

    return (
        <>
            <div id="studio-cell" style={containerStyle}>
                {shouldUseHls ? (
                    <video
                        key={`hls-${playerKey}`}
                        ref={hlsVideoRef}
                        id="studio-cell-video-hls"
                        className="jitsiTrack large h-100"
                        autoPlay={true}
                        playsInline={true}
                        style={{
                            ...backgroundStyle,
                            width: '100%',
                        }}
                        crossOrigin="anonymous"
                    />
                ) : (
                    <>
                        <video
                            id="studio-cell-video"
                            className="jitsiTrack large h-100"
                            autoPlay={true}
                            style={{
                                ...backgroundStyle,
                                width: '100%',
                            }}
                            crossOrigin="anonymous"
                            ref={studioElemRef}
                        />
                        <audio id="studio-cell-audio" className="jitsiTrack" autoPlay={true}/>
                    </>
                )}
                <img
                    alt={'back-up-img'}
                    src={StudioWaitImg}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'fill',
                        zIndex: 1,
                    }}
                />
                {shouldUseHls && <NetworkStatus status={status}/>}
                {(streamingPollingData?.broadcast?.link_list?.length ?? 0) > 0 && (
                    <>
                        <ConnectionStatus/>
                        <StationBadge region={currentSchedule?.region}/>
                    </>
                )}
            </div>
        </>
    );
};

export default StudioCell;
